import React from 'react';
import Modal from './Modal';
import { ModalProps, ModalSize } from './Modal.types';
import { Icon, IconName } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { ReportStatus } from '../../types/reports';
import { useCanadianConsentSubtitle } from '../../hooks/useCanadianConsentSubtitle';

export interface SubmissionMethodOption {
  /**
   * Icon to display for this option
   */
  icon: IconName | string;
  /**
   * Label for the option
   */
  label: string;
  /**
   * Function to call when this option is selected
   */
  onClick: () => void;
  /**
   * Whether this option is primary (highlighted)
   */
  isPrimary?: boolean;
}

export interface SubmissionMethodModalProps extends Omit<ModalProps, 'children'> {
  /**
   * Options for submission methods
   */
  options: SubmissionMethodOption[];
  /**
   * Optional text for the "report zero" link
   */
  reportZeroText?: string;
  /**
   * Optional function to call when the "report zero" link is clicked
   */
  onReportZero?: () => void;
  reportStatus?: ReportStatus;
  /**
   * Whether the shelter is Canadian — shows opt-in guide link when learn-more copy exists
   */
  isCanadian?: boolean;
}

/**
 * Modal for selecting a submission method for pet parent reports
 */
const SubmissionMethodModal: React.FC<SubmissionMethodModalProps> = ({
  title,
  eyebrow,
  secondaryEyebrow,
  isOpen,
  onClose,
  options,
  reportZeroText,
  onReportZero,
  size = ModalSize.Medium,
  showCloseButton = true,
  className = '',
  reportStatus = undefined,
  isCanadian = false,
}) => {
  const { t } = useTranslation();
  const subtitle = useCanadianConsentSubtitle(isCanadian);

  return (
    <Modal
      title={title ?? t(TranslationKeyEnum.SelectSubmissionMethod)}
      eyebrow={eyebrow ?? t(TranslationKeyEnum.PetParentReport)}
      secondaryEyebrow={secondaryEyebrow}
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      showCloseButton={showCloseButton}
      className={className}
      subtitle={subtitle}
    >
      <div className="flex flex-col gap-4 w-full">
        {options.map((option, index) => (
          <button
            key={index}
            className={`
              bg-gray-100 hover:bg-gray-200 border-none rounded-2xl cursor-pointer flex justify-center items-center 
              p-6 h-[180px] transition-all duration-200 ease-in-out w-full focus:outline-none focus:ring-2 focus:ring-blue-600
              ${option.isPrimary ? 'bg-blue-50 hover:bg-blue-100 border border-blue-200' : ''}
            `}
            onClick={option.onClick}
          >
            <div className="flex items-center gap-2">
              <Icon className="text-primary" icon={option.icon} size={24} />
              <span className="font-montserrat font-semibold text-base leading-[1.125em] text-primary">
                {option.label}
              </span>
            </div>
          </button>
        ))}
      </div>
      {/* showing zero report submission if the report is not submitted */}
      {reportStatus !== ReportStatus.Submitted &&
        reportStatus !== ReportStatus.Late &&
        onReportZero && (
          <div className="mt-6 flex justify-center">
            <button
              className="bg-transparent border-none cursor-pointer font-montserrat font-medium text-sm leading-[1.375em] text-black/90 
              p-0 pb-3 hover:underline focus:outline-none focus:underline"
              onClick={onReportZero}
            >
              {reportZeroText ?? t(TranslationKeyEnum.ReportZeroPetParents)}
            </button>
          </div>
        )}
    </Modal>
  );
};

export default SubmissionMethodModal;
